/**
 * Pulls a user-facing message out of an axios-style API error, falling back
 * to the provided default when the response carries none.
 */
export const extractApiErrorMessage = (error: unknown, fallback: string): string => {
    if (!error || typeof error !== 'object') return fallback;

    const response = 'response' in error ? error.response : undefined;
    if (!response || typeof response !== 'object') return fallback;

    const data = 'data' in response ? response.data : undefined;
    if (!data || typeof data !== 'object') return fallback;

    if ('errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
        return String(data.errors[0]);
    }

    if ('description' in data && typeof data.description === 'string') {
        return data.description;
    }

    if ('message' in data && typeof data.message === 'string') {
        return data.message;
    }

    return fallback;
};

/** HTTP status of an axios-style error, when it carries a response at all. */
export const getErrorStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') return undefined;

    return (error as { response?: { status?: number } }).response?.status;
};

export const isUnauthorizedError = (error: unknown): boolean => getErrorStatus(error) === 401;

/**
 * A 4xx is the server's final answer — retrying an expired session, a missing
 * record or a blocked plan only buys the user seconds of spinner.
 */
export const isClientError = (error: unknown): boolean => {
    const status = getErrorStatus(error);

    return status !== undefined && status >= 400 && status < 500;
};
